/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useMemo } from 'react';
import { TokenConfig } from '../types/tokens';
import { 
    SOL_MINT_FEE, SUI_MINT_FEE, SOL_OPTION_FEE, SUI_OPTION_FEE,
    SOL_RENT_PER_BYTE, SOL_MINT_ACCOUNT_SIZE, SOL_METADATA_ACCOUNT_BASE_SIZE,
    SOL_TX_FEE, SOL_NFT_MINT_FEE 
} from '../lib/constants';
import { DeploymentCostEstimate } from '../types/fees';

export const useDeploymentCalculator = (config: Partial<TokenConfig>) => {
    
    const estimate = useMemo((): DeploymentCostEstimate => {
        const isMainnet = config.network === 'mainnet';
        const isSol = config.chain === 'SOL';
        const isNft = config.tags?.includes('NFT');
        
        // Devnet deployments are free of service fees.
        if (!isMainnet) {
            return { base: 0, storage: 0, network: 0, extensions: 0, total: 0 };
        }

        const baseCost = isNft 
            ? SOL_NFT_MINT_FEE // 0.25 SOL for NFTs
            : (isSol ? SOL_MINT_FEE : SUI_MINT_FEE); // 0.1 SOL or 50 SUI for Tokens
        
        // 1. Calculate Storage Rent (Solana Only Logic primarily)
        let storageRent = 0;
        if (isSol) {
            // Estimate Metadata Account Size: Base + Name + Symbol + URI + Socials
            const basicMetaLen = (config.name?.length || 0) + (config.ticker?.length || 0) + 200; // 200 for URI buffer
            
            // Calculate length of optional social fields
            const socialsLen = (config.website?.length || 0) + 
                               (config.twitter?.length || 0) + 
                               (config.telegram?.length || 0) + 
                               (config.discord?.length || 0);

            // Total Size = Mint Account + Metadata Account
            // Note: On-chain metadata often reserves space, but we calculate the rent required for the actual data payload
            let totalBytes = SOL_MINT_ACCOUNT_SIZE + Math.min(basicMetaLen + socialsLen, SOL_METADATA_ACCOUNT_BASE_SIZE);
            
            if (isNft) {
                totalBytes += 282; // Add rent for Metaplex Master Edition V2 Account
            }
            storageRent = totalBytes * SOL_RENT_PER_BYTE; 
        } else {
            // SUI storage is usually rebated, simplified flat estimate
            storageRent = 0.05; 
        }

        // 2. Calculate Extensions Cost (Not applicable for NFTs)
        let extensionCost = 0;
        if (isSol && !isNft && config.tokenStandard === 'TOKEN_2022' && config.extensions) {
            const optionCost = SOL_OPTION_FEE;
            
            // Transfer fee
            if (config.extensions.transferFeeBasisPoints && config.extensions.transferFeeBasisPoints > 0) {
                extensionCost += optionCost; 
                storageRent += (108 * SOL_RENT_PER_BYTE); // TransferFeeConfig size
            }
            // Interest rate
            if (config.extensions.interestRate && config.extensions.interestRate > 0) {
                extensionCost += optionCost;
                storageRent += (52 * SOL_RENT_PER_BYTE); // InterestBearingConfig
            }
            // Immutable Metadata (as a paid feature for Tokens)
            // For NFTs, immutability is standard/expected, so usually no extra fee
            if (config.extensions.immutable) {
                extensionCost += optionCost;
            }
            // Fixed Supply (Revoke Mint)
            if (config.extensions.revokeMint) {
                extensionCost += optionCost;
            }
        } else if (!isSol && config.extensions) { // SUI fees
            // Mock SUI extension fees
            if (config.extensions.transferFeeBasisPoints && config.extensions.transferFeeBasisPoints > 0) extensionCost += SUI_OPTION_FEE;
        }


        // 3. Network Transaction Fees (Signatures)
        // NFTs require more instructions/signatures
        const txCount = isNft ? 5 : 3; 
        
        let networkFee = 0;
        if (isSol) {
            let feePerSig = SOL_TX_FEE; // Baseline: 0.000005 SOL

            // Dynamic Fee Estimation:
            // Token-2022 transactions with active extensions consume more Compute Units (CU).
            // We estimate a higher fee (Priority Fee) for these complex transactions to ensure reliability.
            if (!isNft && config.tokenStandard === 'TOKEN_2022') {
                const hasActiveExtensions = extensionCost > 0 || config.extensions?.revokeMint;
                if (hasActiveExtensions) {
                    feePerSig = 0.00001; // Increase to 0.00001 SOL per signature
                }
            }
            
            networkFee = txCount * feePerSig;
        } else {
            networkFee = 0.02; // SUI Gas
        }

        return {
            base: baseCost,
            storage: parseFloat(storageRent.toFixed(5)),
            network: parseFloat(networkFee.toFixed(6)),
            extensions: parseFloat(extensionCost.toFixed(4)),
            total: parseFloat((baseCost + storageRent + networkFee + extensionCost).toFixed(4))
        };
    }, [config]);

    return estimate;
};